/**
 * Der Scroll-Takt: wie flüssig läuft ein Wisch wirklich? (02.10.2026, Ticket 2912)
 *
 * Martin, iPhone: Scrollen in den Einstellungen sei „total unflüssig, ruckelt, springt wild
 * herum“ — dann: überall, wo es ein Scrollfeld gibt. Die Scroll-Wache (lib/scroll-wache.ts) sieht
 * nur SPRÜNGE (wo steht die Fläche), nie die BILDZEIT (wie oft wird gezeichnet). „Ruckelt“ ist aber
 * genau das: Bilder, die ausfallen. Bisher hat das niemand gemessen; das Ticket 2873 wurde mit
 * Vermutungen „repariert“ und nie belegt. Diese Messung liefert beim nächsten Wisch die Zahlen.
 *
 * ## Was gemessen wird
 *
 * Ein Wisch beginnt mit dem ersten Scroll-Ereignis einer Fläche und endet 250 ms nach dem letzten.
 * Dazwischen läuft `requestAnimationFrame` (nur dann — im Ruhezustand kostet die Messung nichts) und
 * hält die Zeitpunkte fest. Aus den Abständen entstehen: Bilder, Dauer, mittlere Bildzeit, längste
 * Lücke und wie viele Lücken über 34 / 50 / 100 ms lagen (≥ 2 / 3 / 6 ausgefallene Bilder bei 60 Hz).
 * Dazu aus den Scroll-Ereignissen selbst: Zahl der Ereignisse, größter Schritt, Richtungswechsel,
 * zurückgelegter Weg. Und die Größe der Seite: Knoten im Dokument, im Chat, Blasen.
 *
 * ## Was die Zahlen sagen — und was nicht
 *
 * - Viele Lücken ≥ 50 ms, während Martin Ruckeln spürt: Die Hauptschleife der Seite stockt (Skript,
 *   Layout, Stil). Dann hilft die Zeile `art=einst-neu` (wie oft wird der Körper neu gebaut) und die
 *   Spur der Mutationen der Wache.
 * - Gleichmäßige Bilder (Lücken 0), und es ruckelt trotzdem: Das Stocken liegt NICHT im Skript,
 *   sondern beim Zeichnen oder im System (Ebenen, Unschärfe unter dem Dock, Speicherdruck, Telefon
 *   ist heiß). rAF sieht das Zeichnen selbst nicht; das ist die Grenze dieser Messung.
 * - Große Schritte mit gleichmäßigem Takt: Die Fläche selbst springt (Programm schreibt `scrollTop`
 *   oder Inhalt rutscht) — dann stehen `maxSchritt` und `wechsel` hier und die Zeile der Wache dort.
 *
 * Seit pre.3612 dazu (nur im Chat): `leer=<n>/<geprüft>` — in wie vielen Bildern lag im Sichtfeld keine Blase
 * (`leerImSichtfeld`). Seit pre.3607 dazu: `schreiber=` — wer während des Wischs (Finger, Nachlaufen, Ausfedern) programmatisch
 * `scrollTop` geschrieben hat, je Name gezählt (soll `-` sein; das Zuckeln am iPhone ist genau so ein
 * Schreiben), und `aufschub=` — wer gewartet hat, bis es still ist (lib/scroll-ruhe.ts).
 *
 * ⚠️ Keine Inhalte: nur Namen von Flächen, Zahlen. Höchstens 6 Zeilen je Minute für Wische mit
 *    Lücken; ein glatter Wisch als Vergleich höchstens alle 2 Minuten JE FLÄCHE (seit pre.3620) — ohne
 *    den Vergleich wüsste man nie, wie ein guter Wisch auf diesem Gerät aussieht.
 * ⚠️ Gemessen wird passiv (Scroll-Ereignis im Capture, `passive`). Nichts wird angefasst.
 */
import { drosselErlaubt, leseEingabe, nameVon } from "./scroll-wache.js";
import { meldeSpur } from "./mikro-spur.js";
import { gesteuert } from "./ruckel-schalter.js";
import { bucheBildrate } from "./bildrate.js";
import { geraeteSuffix, starteGeraeteZustand } from "./geraete-zustand.js";
import { schreiberSeit, starteScrollRuhe } from "./scroll-ruhe.js";

/** Lücken ab so viel Millisekunden zählen (2 / 3 / 6 Bilder bei 60 Hz). */
const LUECKE_A_MS = 34;
const LUECKE_B_MS = 50;
const LUECKE_C_MS = 100;
/** So lange ohne Scroll-Ereignis (ms) gilt der Wisch als zu Ende. */
const WISCH_STILL_MS = 250;
/** Kürzer als das (Bilder / ms) ist es kein Wisch, sondern ein Anstoß. */
export const WISCH_MIN_BILDER = 12;
const WISCH_MIN_MS = 250;
/** Längstens so lange (ms) läuft EINE Messung — danach beginnt die nächste mit dem nächsten Ereignis. */
const WISCH_MAX_MS = 20_000;
/** Ein glatter Wisch als Vergleich höchstens alle … ms. */
export const GLATT_ALLE_MS = 120_000;
/** Schritte unter dieser Größe zählen nicht als Richtungswechsel (Rauschen, Rundung). */
const RICHTUNG_AB_PX = 5;

export interface Takt {
  bilder: number;
  dauerMs: number;
  /** Mittlerer Abstand zweier Bilder (Median). */
  medianMs: number;
  maxLueckeMs: number;
  luecken34: number;
  luecken50: number;
  luecken100: number;
}

/** Aus den Zeitpunkten der Bilder (ms, aufsteigend) den Takt rechnen. Weniger als 3 Zeitpunkte: keine Aussage. */
export function bewerteTakt(zeiten: readonly number[]): Takt | null {
  if (zeiten.length < 3) return null;
  const luecken: number[] = [];
  for (let i = 1; i < zeiten.length; i++) luecken.push((zeiten[i]) - (zeiten[i - 1]));
  const sortiert = [...luecken].sort((a, b) => a - b);
  const median = sortiert[Math.floor(sortiert.length / 2)];
  return {
    bilder: zeiten.length,
    dauerMs: Math.round((zeiten[zeiten.length - 1]) - (zeiten[0])),
    medianMs: Math.round(median * 10) / 10,
    maxLueckeMs: Math.round(Math.max(...luecken)),
    luecken34: luecken.filter((l) => l > LUECKE_A_MS).length,
    luecken50: luecken.filter((l) => l > LUECKE_B_MS).length,
    luecken100: luecken.filter((l) => l > LUECKE_C_MS).length,
  };
}

/** Ruckelt dieser Wisch? Eine Lücke über 50 ms, oder mindestens drei über 34 ms. */
export function ruckelt(t: Takt): boolean {
  return t.luecken50 >= 1 || t.luecken34 >= 3;
}

/** Ein Wisch ist lang genug, um etwas zu sagen. */
export function langGenug(t: Takt): boolean {
  return t.bilder >= WISCH_MIN_BILDER && t.dauerMs >= WISCH_MIN_MS;
}

/** Was die Scroll-Ereignisse eines Wischs selbst sagen. */
export interface Wisch {
  ereignisse: number;
  maxSchritt: number;
  wechsel: number;
  start: number;
  oben: number;
  letzterSchritt: number;
}

export function neuerWisch(oben: number): Wisch {
  return { ereignisse: 1, maxSchritt: 0, wechsel: 0, start: oben, oben, letzterSchritt: 0 };
}

/** Ein Scroll-Ereignis verbuchen. Reine Rechnung. */
export function buchePunkt(w: Wisch, oben: number): Wisch {
  const schritt = oben - w.oben;
  const gegen =
    Math.abs(schritt) >= RICHTUNG_AB_PX &&
    Math.abs(w.letzterSchritt) >= RICHTUNG_AB_PX &&
    Math.sign(schritt) !== Math.sign(w.letzterSchritt);
  return {
    ereignisse: w.ereignisse + 1,
    maxSchritt: Math.max(w.maxSchritt, Math.abs(schritt)),
    wechsel: w.wechsel + (gegen ? 1 : 0),
    start: w.start,
    oben,
    letzterSchritt: Math.abs(schritt) >= RICHTUNG_AB_PX ? schritt : w.letzterSchritt,
  };
}

type Meldeart = "ruckelt" | "glatt";

/**
 * Ein glatter Vergleichs-Wisch braucht mindestens so viele Scroll-Ereignisse. (02.10.2026, pre.3620)
 * Ein einzelnes programmatisches Scrollen (Klapper, Nachziehen: `ereign=1 weg=0`) zählt 250 ms Bilder und
 * ist sonst „lang genug" — es verbrauchte den Platz des glatten Vergleichs, bevor jemand wischte.
 */
export const GLATT_MIN_EREIGNISSE = 3;

/** Was `sollMelden` sich merkt: die Zeiten der letzten Zeilen (Drossel) und je Fläche den letzten glatten Wisch. */
interface MeldeStand {
  zeiten: number[];
  /** Je Fläche (Name aus `nameVon`): wann zuletzt ein glatter Wisch ins Protokoll kam (ms). */
  letzteGlatte: Map<string, number>;
}

/**
 * Soll dieser Wisch ins Protokoll? Mit Lücken oder mit einem Schreiber mitten im Wisch: ja (höchstens
 * `MAX_ZEILEN_JE_MINUTE` je Minute, die Grenze der Wache). Ohne: als Vergleich höchstens alle `GLATT_ALLE_MS`
 * JE FLÄCHE und nur mit mindestens `GLATT_MIN_EREIGNISSE` Ereignissen.
 *
 * ⚠️ 02.10.2026, 21:33 — bis pre.3608 galt die Frist für alle Flächen zusammen. Martin wischte in den
 *    Einstellungen und klagte über Ruckeln; im Journal stand für sie KEINE Takt-Zeile: Um 21:32:53 hatte ein
 *    einzelnes Scroll-Ereignis im Chat (`ereign=1 weg=0`, das Zuklappen beim Zurückkehren) den glatten
 *    Vergleich für zwei Minuten verbraucht. Seine Wische waren laut rAF glatt — und wurden verschwiegen.
 */
export function sollMelden(
  t: Takt,
  jetzt: number,
  stand: MeldeStand,
  /** Hat während des Wischs jemand `scrollTop` geschrieben? Dann zählt er wie ein ruckelnder (pre.3607). */
  mitSchreiber = false,
  wo: { ziel?: string; ereignisse?: number } = {},
): Meldeart | null {
  if (!langGenug(t)) return null;
  if (ruckelt(t)) return drosselErlaubt(stand.zeiten, jetzt) ? "ruckelt" : null;
  if (mitSchreiber) return drosselErlaubt(stand.zeiten, jetzt) ? "glatt" : null;
  if ((wo.ereignisse ?? GLATT_MIN_EREIGNISSE) < GLATT_MIN_EREIGNISSE) return null;
  const ziel = wo.ziel ?? "-";
  const letzte = stand.letzteGlatte.get(ziel);
  if (letzte === undefined || jetzt - letzte >= GLATT_ALLE_MS) {
    stand.letzteGlatte.set(ziel, jetzt);
    return "glatt";
  }
  return null;
}

/** Wie groß die Seite ist: Knoten im Dokument, im Chat, Blasen im Chat (nur Zahlen). */
function seitenGroesse(): { knoten: number; chatKnoten: number; blasen: number; ansicht: string } {
  const chat = document.getElementById("chat");
  const offen = Array.from(document.querySelectorAll<HTMLElement>(".view")).find((v) => !v.hidden && v.offsetParent !== null);
  return {
    knoten: document.getElementsByTagName("*").length,
    chatKnoten: chat ? chat.getElementsByTagName("*").length : 0,
    blasen: chat ? chat.children.length : 0,
    ansicht: offen?.id ?? "-",
  };
}

/**
 * Die Zeile für das Protokoll. Reine Rechnung. `seitRueckkehrS` = Sekunden seit die Seite zuletzt wieder
 * sichtbar wurde (−1: seit dem Start nie weg gewesen). Grund: Am 02.10. um 06:45 kamen sechs Scroll-Ereignisse
 * im Abstand von 56–68 ms (statt 16) genau in dem Moment, als die App aus dem Hintergrund zurückkehrte
 * (`[sichtbar-d] hidden deltaMs=1266012`) — ob Ruckeln nur dort auftritt, sagt diese Zahl.
 */
export function taktZeile(
  art: Meldeart,
  ziel: string,
  eingabe: string,
  t: Takt,
  w: Wisch,
  hoehe: number,
  sicht: number,
  groesse: { knoten: number; chatKnoten: number; blasen: number; ansicht: string },
  seitRueckkehrS = -1,
  schreib: { schreiber: string; aufschub: string } = { schreiber: "-", aufschub: "-" },
  leer = "-",
): string {
  return (
    `art=takt glatt=${art === "glatt" ? "ja" : "nein"} ziel=${ziel} ansicht=${groesse.ansicht} eingabe=${eingabe} ` +
    `dauer=${t.dauerMs} bilder=${t.bilder} median=${t.medianMs} max=${t.maxLueckeMs} ` +
    `l34=${t.luecken34} l50=${t.luecken50} l100=${t.luecken100} ` +
    `ereign=${w.ereignisse} maxSchritt=${Math.round(w.maxSchritt)} wechsel=${w.wechsel} weg=${Math.round(w.oben - w.start)} ` +
    `hoehe=${Math.round(hoehe)} sicht=${Math.round(sicht)} ` +
    `knoten=${groesse.knoten} chatKnoten=${groesse.chatKnoten} blasen=${groesse.blasen} seitRueckkehr=${seitRueckkehrS} ` +
    `schreiber=${schreib.schreiber} aufschub=${schreib.aufschub} leer=${leer}`
  );
}

/** Die Treffertests laufen nur in jedem n-ten Bild (Schritt C: vorher in jedem — 9 `elementFromPoint` je Bild). */
export const LEER_JEDES_N_BILD = 10;

/** Ob im `n`-ten geprüften Bild (ab 0) die Treffertests laufen: das erste, dann jedes `LEER_JEDES_N_BILD`-te. Reine Rechnung. */
export function leerPruefenJetzt(n: number): boolean {
  return n % LEER_JEDES_N_BILD === 0;
}

/**
 * Liegt im Sichtfeld des Chats gerade KEINE Blase? (pre.3612) Martin, iPhone, 02.10. ~22:25: Beim schnellen
 * Hochwischen „verschwinden die Chat-Elemente, bis der Verlauf stehen bleibt“. Im echten WebKit fehlte dabei im
 * Dokument nichts (tests/browser/scroll-iphone-webkit.mts, Fall h). Fehlt am Gerät das BILD (WebKit zeichnet
 * die Kacheln nicht nach), sagt diese Prüfung „nicht leer“ — fehlt der INHALT, sagt sie „leer“. Neun Punkte im
 * Raster (Viertel, Mitte, Dreiviertel); ein Treffer auf ein Kind des Chats (Blase, Karte, Trenner) genügt.
 * Kostet neun Treffertests (je zehntes Bild, `leerPruefenJetzt`), nur während eines Wischs im Chat.
 */
export function leerImSichtfeld(chat: HTMLElement, treffer: (x: number, y: number) => Element | null): boolean {
  const r = chat.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return false;
  for (const fy of [0.25, 0.5, 0.75]) {
    for (const fx of [0.25, 0.5, 0.75]) {
      const e = treffer(r.left + r.width * fx, r.top + r.height * fy);
      if (e && e !== chat && chat.contains(e)) return false;
    }
  }
  return true;
}

interface Sitzung {
  el: Element;
  zeiten: number[];
  wisch: Wisch;
  eingabe: string;
  letzterScroll: number;
  raf: number;
  /** Beginn des Wischs (performance.now) — ab da zählen die Schreiber. */
  beginn: number;
  /** Nur im Chat: Bilder, in denen an keinem der neun Prüfpunkte eine Blase lag (`leerImSichtfeld`), und geprüfte Bilder. */
  leer: number;
  geprueft: number;
  /** Alle Bilder des Wischs im Chat (Zähler für die Stichprobe). */
  bilder: number;
}

let laeuft = false;

/** Den Takt-Messer einschalten (einmal). */
export function starteScrollTakt(): void {
  if (laeuft || typeof document === "undefined" || typeof requestAnimationFrame === "undefined") return;
  laeuft = true;
  // Die Regel, deren Zähler hier in die Zeile kommen, muss jede Berührung von Anfang an sehen.
  starteScrollRuhe();
  starteGeraeteZustand();
  const stand: MeldeStand = { zeiten: [], letzteGlatte: new Map() };
  let sitzung: Sitzung | null = null;
  let sichtbarAb = -1;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") sichtbarAb = performance.now();
  });

  const beende = (): void => {
    const s = sitzung;
    sitzung = null;
    if (!s) return;
    try {
      const t = bewerteTakt(s.zeiten);
      if (!t || document.visibilityState !== "visible") return;
      // Jeder Wisch zählt für den Stand der Bildrate (lib/bildrate.ts) — auch der, der nicht ins Journal kommt.
      bucheBildrate(t);
      const schreib = schreiberSeit(s.beginn);
      const ziel = nameVon(s.el);
      const art = sollMelden(t, Date.now(), stand, schreib.schreiber !== "-", { ziel, ereignisse: s.wisch.ereignisse });
      if (!art) return;
      const el = s.el as HTMLElement;
      meldeSpur("scroll-t", taktZeile(art, ziel, s.eingabe, t, s.wisch, el.scrollHeight, el.clientHeight, seitenGroesse(), sichtbarAb < 0 ? -1 : Math.round((performance.now() - sichtbarAb) / 1000), schreib, s.geprueft > 0 ? `${s.leer}/${s.geprueft}` : "-") + geraeteSuffix());
    } catch {
      /* Messen darf nie stören. */
    }
  };

  const bild = (zeit: number): void => {
    const s = sitzung;
    if (!s) return;
    s.zeiten.push(zeit);
    if ((s.el as HTMLElement).id === "chat") {
      // Nur jedes LEER_JEDES_N_BILD-te Bild messen: `leer=<n>/<geprüft>` zählt dann Stichproben (gleiche Aussage, ein Zehntel der Kosten).
      if (leerPruefenJetzt(s.bilder++)) {
        s.geprueft++;
        if (leerImSichtfeld(s.el as HTMLElement, (x, y) => document.elementFromPoint(x, y))) s.leer++;
      }
    }
    if (performance.now() - s.letzterScroll > WISCH_STILL_MS || zeit - (s.zeiten[0]) > WISCH_MAX_MS) {
      beende();
      return;
    }
    s.raf = requestAnimationFrame(bild);
  };

  document.addEventListener(
    "scroll",
    gesteuert("wachen", (ev: Event) => {
      try {
        if (document.visibilityState !== "visible") return;
        const ziel = ev.target;
        const el = ziel instanceof Element ? ziel : document.scrollingElement;
        if (!el) return;
        const jetzt = performance.now();
        if (sitzung && sitzung.el !== el) return; // ein Wisch zur Zeit
        if (!sitzung) {
          sitzung = {
            el,
            zeiten: [],
            wisch: neuerWisch(el.scrollTop),
            eingabe: leseEingabe(),
            letzterScroll: jetzt,
            raf: 0,
            beginn: jetzt,
            leer: 0,
            geprueft: 0,
            bilder: 0,
          };
          sitzung.raf = requestAnimationFrame(bild);
          return;
        }
        sitzung.letzterScroll = jetzt;
        sitzung.wisch = buchePunkt(sitzung.wisch, el.scrollTop);
      } catch {
        /* Messen darf nie stören. */
      }
    }),
    { capture: true, passive: true },
  );
}
